import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../hooks/useTranslation';
import { fetchCmsLinks } from '../api/api';
import RTLContext from '../context/RTLContext';
import logger from '../utils/logger';
import '../css/Footer.css';

const Footer = () => {
  const t = useTranslation();
  const rtlContext = useContext(RTLContext);
  const reduxLang = useSelector((state) => state.user?.lang);
  const lang = rtlContext?.languageCode || reduxLang || 'EN';

  const [cmsLinks, setCmsLinks] = useState({});

  useEffect(() => {
    let isMounted = true;
    fetchCmsLinks(lang)
      .then((links) => {
        if (isMounted) {
          setCmsLinks(links || {});
        }
      })
      .catch((err) => {
        logger.error('Failed to load CMS links in Footer:', err);
      });
    return () => {
      isMounted = false;
    };
  }, [lang]);

  const year = new Date().getFullYear();

  const openCookieBanner = (e) => {
    e.preventDefault();
    if (window.RocketBPMCookieBanner && typeof window.RocketBPMCookieBanner.open === 'function') {
      window.RocketBPMCookieBanner.open();
    } else {
      // Dispatch custom event for RB-NEW-2 / RB-190 listener
      const event = new CustomEvent('open-cookie-banner');
      window.dispatchEvent(event);
    }
  };

  const imprintLink = cmsLinks.imprint || {
    url:
      t('footer.imprint.url') !== 'footer.imprint.url' ? t('footer.imprint.url') : '/page/imprint',
    title:
      t('footer.imprint.label') !== 'footer.imprint.label' ? t('footer.imprint.label') : 'Imprint',
  };
  const privacyLink = cmsLinks.privacy || {
    url: t('privacy_url') !== 'privacy_url' ? t('privacy_url') : '/page/privacy-policy',
    title:
      t('footer.privacy.label') !== 'footer.privacy.label'
        ? t('footer.privacy.label')
        : 'Privacy Policy',
  };
  const termsLink = cmsLinks.terms || {
    url: t('terms_url') !== 'terms_url' ? t('terms_url') : '/page/terms',
    title:
      t('footer.terms.label') !== 'footer.terms.label'
        ? t('footer.terms.label')
        : 'Terms and Conditions',
  };

  const standardKeys = ['imprint', 'privacy', 'terms'];
  const extraCmsLinks = Object.entries(cmsLinks)
    .filter(([key]) => !standardKeys.includes(key.toLowerCase()))
    .map(([, data]) => data);

  return (
    <footer className="app-footer">
      <div className="app-footer-bounds">
        <div className="app-footer-row">
          <span>
            © {year} {t('mkburg_process_experiences_gmbh')}
          </span>
          <div className="app-footer-links">
            <a href={imprintLink.url} className="app-footer-link">
              {imprintLink.title}
            </a>
            <span aria-hidden="true" className="app-footer-sep">
              ·
            </span>
            <a href={privacyLink.url} className="app-footer-link">
              {privacyLink.title}
            </a>
            <span aria-hidden="true" className="app-footer-sep">
              ·
            </span>
            <a href={termsLink.url} className="app-footer-link">
              {termsLink.title}
            </a>
            {extraCmsLinks.map((link, idx) => (
              <React.Fragment key={link.url || idx}>
                <span aria-hidden="true" className="app-footer-sep">
                  ·
                </span>
                <a href={link.url} className="app-footer-link">
                  {link.title}
                </a>
              </React.Fragment>
            ))}
            <span aria-hidden="true" className="app-footer-sep">
              ·
            </span>
            <button
              type="button"
              onClick={openCookieBanner}
              className="app-footer-link app-footer-button"
            >
              {t('footer.cookies.label') !== 'footer.cookies.label'
                ? t('footer.cookies.label')
                : 'Cookie Settings'}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
